import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const ToastContainer = styled(motion.div)`
  position: fixed;
  top: 2rem;
  right: 2rem;
  z-index: 1001;
  max-width: 400px;
  width: 100%;
  
  @media (max-width: 768px) {
    top: 1rem;
    right: 1rem;
    left: 1rem;
    max-width: none;
  }
`;

const Toast = styled(motion.div)`
  background: ${props => {
    switch (props.type) {
      case 'success': return 'linear-gradient(135deg, #00b894, #00cec9)';
      case 'error': return 'linear-gradient(135deg, #e74c3c, #fd79a8)';
      case 'warning': return 'linear-gradient(135deg, #fdcb6e, #e17055)';
      default: return 'linear-gradient(135deg, #667eea, #764ba2)';
    }
  }};
  color: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  margin-bottom: 1rem;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ToastContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const ToastIcon = styled.div`
  font-size: 1.5rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
`;

const ToastText = styled.div`
  flex: 1;
`;

const ToastTitle = styled.div`
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 0.3rem;
`;

const ToastMessage = styled.div`
  font-size: 0.9rem;
  opacity: 0.9;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ProgressBar = styled(motion.div)`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 0 0 12px 12px;
`;

const getToastConfig = (type) => {
  switch (type) {
    case 'success':
      return { icon: '✓', title: 'Success!' };
    case 'error':
      return { icon: '✕', title: 'Error' };
    case 'warning':
      return { icon: '⚠', title: 'Warning' };
    default:
      return { icon: 'ℹ', title: 'Info' };
  }
};

const NotificationToast = ({ notifications, onRemove }) => {
  return (
    <ToastContainer>
      <AnimatePresence>
        {notifications.map((notification) => {
          const config = getToastConfig(notification.type);
          
          return (
            <Toast
              key={notification.id}
              type={notification.type}
              initial={{ x: 400, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 400, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              <CloseButton onClick={() => onRemove(notification.id)}>
                ×
              </CloseButton>
              
              <ToastContent>
                <ToastIcon>{config.icon}</ToastIcon>
                <ToastText>
                  <ToastTitle>{config.title}</ToastTitle>
                  <ToastMessage>{notification.message}</ToastMessage>
                </ToastText>
              </ToastContent>
              
              {notification.autoRemove && (
                <ProgressBar
                  initial={{ width: '100%' }}
                  animate={{ width: '0%' }}
                  transition={{ 
                    duration: notification.duration / 1000,
                    ease: 'linear'
                  }}
                />
              )}
            </Toast>
          );
        })}
      </AnimatePresence>
    </ToastContainer>
  );
};

export default NotificationToast;
